import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import DashboardLayout from '../components/DashboardLayout';
import api from '../services/api';
import { fetchUsers } from '../services/user';

const SORT_OPTIONS = [
  { value: 'recent', label: 'Most recent' },
  { value: 'best', label: 'Best reviews' },
  { value: 'worst', label: 'Worst reviews' },
];

const PROGRAM_OPTIONS = [
  { value: 'all', label: 'All programmes' },
  { value: 'general', label: 'General' },
  { value: 'matric', label: 'Matric' },
  { value: 'math', label: 'Math' },
];

const ASSIGNMENT_SCOPE_OPTIONS = [
  { value: 'current', label: 'Current assigned only' },
  { value: 'historical', label: 'Historical' },
];

const DEFAULT_REVIEW_FILTERS = {
  sort: 'recent',
  tutorId: '',
  program: 'all',
  assignmentScope: 'current',
};

const formatName = (user, fallback) => {
  if (!user) return fallback;
  const name = [user.name, user.surname].filter(Boolean).join(' ').trim();
  return name || user.email || fallback;
};

const formatDate = (value) => {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '-';
  return parsed.toLocaleString();
};

const renderStars = (rating) => {
  const safe = Math.max(1, Math.min(5, Number(rating) || 0));
  return `${'★'.repeat(safe)}${'☆'.repeat(5 - safe)}`;
};

const normalizeProgram = (value) => {
  const raw = String(value || 'general').trim().toLowerCase();
  if (raw === 'math') return 'math';
  if (raw === 'matric') return 'matric';
  return 'general';
};

const programLabel = (value) => {
  const normalized = normalizeProgram(value);
  if (normalized === 'math') return 'Math';
  if (normalized === 'matric') return 'Matric';
  return 'General';
};

const Reviews = () => {
  const [sort, setSort] = useState(DEFAULT_REVIEW_FILTERS.sort);
  const [selectedTutorId, setSelectedTutorId] = useState(DEFAULT_REVIEW_FILTERS.tutorId);
  const [selectedProgram, setSelectedProgram] = useState(DEFAULT_REVIEW_FILTERS.program);
  const [assignmentScope, setAssignmentScope] = useState(DEFAULT_REVIEW_FILTERS.assignmentScope);
  const [appliedFilters, setAppliedFilters] = useState(DEFAULT_REVIEW_FILTERS);
  const [tutors, setTutors] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loadingTutors, setLoadingTutors] = useState(true);
  const [loadingReviews, setLoadingReviews] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoadingTutors(true);
        const list = await fetchUsers('tutor');
        if (!mounted) return;
        setTutors(Array.isArray(list) ? list : []);
      } catch (e) {
        if (!mounted) return;
        setError(e?.message || 'Failed to load tutors');
      } finally {
        if (mounted) setLoadingTutors(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoadingReviews(true);
        setError('');
        const params = {
          sort: appliedFilters.sort,
          assignmentScope: appliedFilters.assignmentScope,
        };
        if (appliedFilters.tutorId) params.tutorId = appliedFilters.tutorId;
        const { data } = await api.get('/reviews/admin', { params });
        if (!mounted) return;
        setReviews(Array.isArray(data?.reviews) ? data.reviews : []);
      } catch (e) {
        if (!mounted) return;
        setReviews([]);
        setError(e?.response?.data?.message || e?.message || 'Failed to load reviews');
      } finally {
        if (mounted) setLoadingReviews(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [appliedFilters.sort, appliedFilters.tutorId, appliedFilters.assignmentScope]);

  const tutorOptions = useMemo(
    () =>
      tutors
        .slice()
        .sort((a, b) => formatName(a, '').localeCompare(formatName(b, ''), undefined, { sensitivity: 'base' }))
        .map((tutor) => ({
          value: tutor._id,
          label: formatName(tutor, 'Tutor'),
          email: tutor.email || '',
        })),
    [tutors]
  );

  const activeTutor = useMemo(
    () => tutorOptions.find((option) => option.value === appliedFilters.tutorId) || null,
    [tutorOptions, appliedFilters.tutorId]
  );

  const reviewsByProgram = useMemo(() => {
    return reviews.reduce(
      (acc, review) => {
        const program = normalizeProgram(review?.session?.program);
        acc[program].push(review);
        return acc;
      },
      { general: [], matric: [], math: [] }
    );
  }, [reviews]);

  const filteredReviews = useMemo(() => {
    if (appliedFilters.program === 'all') return reviews;
    return reviewsByProgram[appliedFilters.program] || [];
  }, [reviews, reviewsByProgram, appliedFilters.program]);

  const filteredSummary = useMemo(() => {
    const totalReviews = filteredReviews.length;
    const totalRating = filteredReviews.reduce((sum, review) => sum + Number(review?.rating || 0), 0);
    return {
      totalReviews,
      averageRating: totalReviews ? totalRating / totalReviews : 0,
    };
  }, [filteredReviews]);

  const handleSearch = () => {
    setAppliedFilters({
      sort,
      tutorId: selectedTutorId,
      program: selectedProgram,
      assignmentScope,
    });
  };

  const handleClearFilters = () => {
    setSort(DEFAULT_REVIEW_FILTERS.sort);
    setSelectedTutorId(DEFAULT_REVIEW_FILTERS.tutorId);
    setSelectedProgram(DEFAULT_REVIEW_FILTERS.program);
    setAssignmentScope(DEFAULT_REVIEW_FILTERS.assignmentScope);
    setAppliedFilters(DEFAULT_REVIEW_FILTERS);
  };

  return (
    <DashboardLayout>
      <Stack spacing={3}>
        <Card>
          <CardContent>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} useFlexGap flexWrap="wrap">
              <FormControl fullWidth sx={{ minWidth: { md: 220 }, flex: { md: '1 1 220px' } }}>
                <InputLabel id="reviews-sort-label">Sort</InputLabel>
                <Select
                  labelId="reviews-sort-label"
                  value={sort}
                  label="Sort"
                  onChange={(e) => setSort(e.target.value)}
                  disabled={loadingReviews}
                >
                  {SORT_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth sx={{ minWidth: { md: 320 }, flex: { md: '1 1 320px' } }}>
                <InputLabel id="reviews-tutor-label">Tutor</InputLabel>
                <Select
                  labelId="reviews-tutor-label"
                  value={selectedTutorId}
                  label="Tutor"
                  onChange={(e) => setSelectedTutorId(e.target.value)}
                  disabled={loadingTutors || loadingReviews}
                >
                  <MenuItem value="">All tutors</MenuItem>
                  {tutorOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      <Stack spacing={0.25}>
                        <Typography variant="body2" fontWeight={600}>
                          {option.label}
                        </Typography>
                        {option.email ? (
                          <Typography variant="caption" color="text.secondary">
                            {option.email}
                          </Typography>
                        ) : null}
                      </Stack>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth sx={{ minWidth: { md: 220 }, flex: { md: '1 1 220px' } }}>
                <InputLabel id="reviews-program-label">Programme</InputLabel>
                <Select
                  labelId="reviews-program-label"
                  value={selectedProgram}
                  label="Programme"
                  onChange={(e) => setSelectedProgram(e.target.value)}
                  disabled={loadingReviews}
                >
                  {PROGRAM_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth sx={{ minWidth: { md: 240 }, flex: { md: '1 1 240px' } }}>
                <InputLabel id="reviews-assignment-scope-label">Assignment Scope</InputLabel>
                <Select
                  labelId="reviews-assignment-scope-label"
                  value={assignmentScope}
                  label="Assignment Scope"
                  onChange={(e) => setAssignmentScope(e.target.value)}
                  disabled={loadingReviews}
                >
                  {ASSIGNMENT_SCOPE_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                sx={{
                  minWidth: { md: 260 },
                  flex: { md: '1 1 260px' },
                  alignSelf: { xs: 'stretch', md: 'center' },
                }}
              >
                <Button
                  variant="contained"
                  onClick={handleSearch}
                  disabled={loadingReviews}
                  sx={{
                    minWidth: 140,
                    fontWeight: 800,
                  }}
                >
                  Search Reviews
                </Button>
                <Button variant="outlined" onClick={handleClearFilters} disabled={loadingReviews}>
                  Clear Filters
                </Button>
              </Stack>
            </Stack>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Active filters:{' '}
              <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
                {activeTutor?.label || 'All tutors'}
              </Box>
              {' · '}
              {PROGRAM_OPTIONS.find((option) => option.value === appliedFilters.program)?.label || 'All programmes'}
              {' · '}
              {ASSIGNMENT_SCOPE_OPTIONS.find((option) => option.value === appliedFilters.assignmentScope)?.label ||
                'Current assigned only'}
            </Typography>
          </CardContent>
          {(loadingTutors || loadingReviews) && <LinearProgress />}
        </Card>

        {error ? (
          <Alert severity="error" onClose={() => setError('')}>
            {error}
          </Alert>
        ) : null}

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <Card sx={{ flex: 1 }}>
            <CardContent>
              <Typography variant="overline" color="text.secondary">
                Total Reviews
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 800 }}>
                {filteredSummary.totalReviews}
              </Typography>
            </CardContent>
          </Card>
          <Card sx={{ flex: 1 }}>
            <CardContent>
              <Typography variant="overline" color="text.secondary">
                Average Rating
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 800 }}>
                {filteredSummary.totalReviews ? filteredSummary.averageRating.toFixed(2) : '0.00'} / 5
              </Typography>
            </CardContent>
          </Card>
          <Card sx={{ flex: 1 }}>
            <CardContent>
              <Typography variant="overline" color="text.secondary">
                Active Tutor Filter
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {activeTutor?.label || 'All tutors'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {PROGRAM_OPTIONS.find((option) => option.value === appliedFilters.program)?.label || 'All programmes'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {ASSIGNMENT_SCOPE_OPTIONS.find((option) => option.value === appliedFilters.assignmentScope)?.label ||
                  'Current assigned only'}
              </Typography>
            </CardContent>
          </Card>
        </Stack>

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          {['general', 'matric', 'math'].map((program) => (
            <Card
              key={program}
              sx={{
                flex: 1,
                border: selectedProgram === program ? '2px solid' : '1px solid',
                borderColor: selectedProgram === program ? 'primary.main' : 'divider',
                cursor: 'pointer',
              }}
              onClick={() => setSelectedProgram(program)}
            >
              <CardContent>
                <Typography variant="overline" color="text.secondary">
                  {programLabel(program)}
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 800 }}>
                  {reviewsByProgram[program]?.length || 0}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </Stack>

        <Card>
          <CardHeader
            title="Review List"
            subheader={`${filteredReviews.length} review${filteredReviews.length === 1 ? '' : 's'} shown`}
          />
          <Divider />
          <CardContent sx={{ p: 0 }}>
            <TableContainer sx={{ maxWidth: '100%', overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Programme</TableCell>
                    <TableCell>Tutor</TableCell>
                    <TableCell>Student</TableCell>
                    <TableCell>Session</TableCell>
                    <TableCell>Rating</TableCell>
                    <TableCell>Feedback</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!loadingReviews && filteredReviews.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7}>
                        <Box sx={{ py: 2, textAlign: 'center', color: 'text.secondary' }}>
                          No reviews found for the selected filters.
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : null}

                  {filteredReviews.map((review) => (
                    <TableRow key={review._id} hover>
                      <TableCell>{formatDate(review.createdAt)}</TableCell>
                      <TableCell>
                        <Chip size="small" label={programLabel(review?.session?.program)} />
                      </TableCell>
                      <TableCell>{review?.tutor?.name || 'Tutor'}</TableCell>
                      <TableCell>{review?.student?.name || 'Student'}</TableCell>
                      <TableCell>
                        <Stack spacing={0.25}>
                          <Typography variant="body2" fontWeight={600}>
                            {review?.session?.subject || 'Lesson'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(review?.session?.completedAt)}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant="body2" sx={{ letterSpacing: 1 }}>
                            {renderStars(review.rating)}
                          </Typography>
                          <Chip size="small" label={`${review.rating}/5`} />
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 360 }}>
                        <Typography
                          variant="body2"
                          color={review?.note ? 'text.primary' : 'text.secondary'}
                          sx={{
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                          }}
                        >
                          {review?.note || 'No written feedback'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Stack>
    </DashboardLayout>
  );
};

export default Reviews;
